import React from "react";
import { roundTo } from "../../utils";
import clsx from "clsx";
import { LoanPaymentStatus } from "../../contexts";

type PaymentScheduleRowProps = {
  date: string;
  isLast: boolean;
  isFirst: boolean;
  currencyUnit: string;
  installmentNo: number;
  monthlyPayable: number;
  isReviewScreen?: boolean;
  loanPaymentStatus?: LoanPaymentStatus;
};

const PaymentScheduleRow: React.FC<PaymentScheduleRowProps> = ({
  date,
  isLast,
  isFirst,
  currencyUnit,
  installmentNo,
  monthlyPayable,
  isReviewScreen = true,
  loanPaymentStatus = LoanPaymentStatus.SCHEDULED,
}) => {
  const statusBadge =
    loanPaymentStatus === LoanPaymentStatus.PAID ? (
      <div className="cx-inline-flex cx-items-center cx-w-fit cx-py-1 cx-px-4 cx-rounded-btn cx-text-success-darker cx-bg-success-lighter max-md:cx-text-sm">
        Paid
      </div>
    ) : (
      <div className="cx-inline-flex cx-items-center cx-w-fit cx-py-1 cx-px-4 cx-rounded-btn cx-text-neutral-darker cx-bg-neutral-lighter max-md:cx-text-sm">
        Pending
      </div>
    );
  return (
    <div className="cx-relative cx-flex cx-w-full cx-py-4">
      {isReviewScreen && (
        <div className="cx-absolute max-md:cx-hidden cx-inset-0 cx-flex cx-items-center cx-justify-center cx-left-[22%]">
          {statusBadge}
        </div>
      )}
      <div className="cx-flex cx-items-start cx-gap-3 cx-w-full">
        <div className="cx-relative">
          <div
            className={clsx(
              "cx-h-2.5 cx-w-2.5 cx-mt-2 cx-rounded-full cx-bg-brand-primary-dark",
              {
                "cx-bg-success-dark":
                  loanPaymentStatus === LoanPaymentStatus.PAID,
              }
            )}
          />
          {!isLast && (
            <div
              className={clsx(
                "cx-absolute cx-left-1 cx-top-5 cx-border-l-2 cx-border-neutral-lighter cx-h-[calc(100%-10px)]",
                {
                  "cx-border-success-light":
                    loanPaymentStatus === LoanPaymentStatus.PAID,
                }
              )}
            />
          )}
        </div>
        <div className="cx-flex-1 cx-flex cx-flex-col">
          <div className="cx-flex cx-items-start cx-justify-between cx-gap-4">
            <div
              className={clsx("cx-text-base cx-font-semibold", {
                "cx-text-brand-primary-dark": isFirst,
                "cx-text-neutral-dark": !isFirst,
                "cx-text-success-dark":
                  loanPaymentStatus === LoanPaymentStatus.PAID,
              })}
            >
              {isFirst
                ? "Due on approval"
                : `Due in ${installmentNo} month${
                    installmentNo > 1 ? "s" : ""
                  }`}
            </div>
            <div className="cx-text-lg cx-font-normal cx-inline-flex cx-items-center cx-gap-1">
              {currencyUnit === "AED" ? (
                <div className="cx-font-aed-font"> D </div>
              ) : (
                <span>{currencyUnit}</span>
              )}
              <span>{roundTo(monthlyPayable, 2).toLocaleString()}</span>
            </div>
          </div>
          {isReviewScreen && (
            <div className="md:cx-hidden cx-mt-2 cx--ml-[6px]">
              {statusBadge}
            </div>
          )}
          {isFirst ? (
            <div
              className={clsx("cx-text-sm cx-font-medium", {
                "cx-text-neutral-darker":
                  loanPaymentStatus !== LoanPaymentStatus.PAID,
                "cx-text-success-dark":
                  loanPaymentStatus === LoanPaymentStatus.PAID,
              })}
            >
              {loanPaymentStatus !== LoanPaymentStatus.PAID &&
                "To be debited once the contract is signed"}
            </div>
          ) : (
            <div className="cx-mt-1 cx-text-base cx-font-semibold cx-text-brand-primary-dark">
              {date}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentScheduleRow;
